import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { translate } from '../i18n';

// ─── RBAC v2 types ───
export type PlatformRole = 'super_admin' | 'user';
export type OrgRole = 'org_admin' | 'maintenance' | 'user' | 'installer_admin' | 'installer_technician';

export interface Membership {
  orgId: number;
  orgSlug: string;
  orgName: string;
  role: OrgRole;
}

// Types
export interface User {
  userId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  // Droits : platformRole (plateforme) + memberships (rôle dans chaque organisation)
  platformRole?: PlatformRole;
  memberships?: Membership[];
  currentOrgId?: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasAccess: (resource: 'wattelzce' | 'localizce') => boolean;
  // RBAC v2 helpers
  currentMembership: Membership | null;
  hasOrgRole: (...allowed: OrgRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage keys
const TOKEN_KEY = 'dashboard_zce_token';
const USER_KEY = 'dashboard_zce_user';

// API base URL for auth (wattelzce service handles auth)
const AUTH_API_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved auth state on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
        
        // Verify token is still valid
        verifyToken(savedToken).catch(() => {
          // Token invalid, clear auth
          logout();
        });
      } catch {
        // Invalid saved data, clear
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    
    setIsLoading(false);
  }, []);

  // Verify token with backend
  const verifyToken = async (authToken: string): Promise<User> => {
    const response = await fetch(`${AUTH_API_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Token invalid');
    }

    const result = await response.json();
    return result.data;
  };

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${AUTH_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errMsg = typeof result.error === 'string' ? result.error : result.error?.message || translate('login_err_failed');
        throw new Error(errMsg);
      }

      const { token: newToken, user: userData } = result.data;

      // Save to state and storage
      setToken(newToken);
      setUser(userData);
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  // Check if user has access to a specific resource
  const hasAccess = useCallback((resource: 'wattelzce' | 'localizce'): boolean => {
    if (!user) return false;
    // Accès décidé par platformRole et les memberships uniquement (plus jamais par l'ancien rôle)
    // Super admin platform role bypasses
    if (user.platformRole === 'super_admin') return true;
    // Installers and clients always have access to wattelzce dashboard
    const orgRole = user.memberships?.find(m => m.orgId === user.currentOrgId)?.role;
    if (orgRole && resource === 'wattelzce') return true;
    return false;
  }, [user]);

  // ─── RBAC v2 helpers ───
  const currentMembership: Membership | null =
    user?.memberships?.find((m) => m.orgId === user.currentOrgId) ?? null;

  const hasOrgRole = useCallback((...allowed: OrgRole[]): boolean => {
    if (!user) return false;
    if (!currentMembership) return false;
    return allowed.includes(currentMembership.role);
  }, [user, currentMembership]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    hasAccess,
    currentMembership,
    hasOrgRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper to get auth token for API calls
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// Helper to create authenticated fetch headers
export function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}
